import { type FormEvent, type ReactElement, useState } from 'react';
import { Button, FormField } from '@/shared/ui';
import { useRequestEmailChange } from '../model/use-request-email-change';

interface EmailChangeFormProps {
  /** 지금 쓰는 이메일 — 같은 주소로는 보낼 수 없다. */
  readonly currentEmail: string;
  /** 비밀번호가 있는 계정이면 현재 비밀번호 확인 칸을 함께 보인다. */
  readonly hasPassword: boolean;
}

/** 이메일 변경 요청 폼 — 새 주소로 확인 메일을 보내고, 같은 자리에 안내 문구를 띄운다. */
export const EmailChangeForm = ({
  currentEmail,
  hasPassword,
}: EmailChangeFormProps): ReactElement => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { emailError, passwordError, formError, isPending, sentTo, submit } =
    useRequestEmailChange();

  const normalizedEmail = email.trim().toLowerCase();
  const isUnchanged =
    normalizedEmail === '' || normalizedEmail === currentEmail.trim().toLowerCase();

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submit(hasPassword ? { email, password } : { email });
  };

  return (
    <form
      aria-label="이메일 변경"
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
    >
      <p className="text-sm text-muted-foreground sm:basis-full">
        변경하면 로그인·알림 이메일이 함께 바뀝니다. 새 주소로 보낸 확인 메일의 링크를 열어야
        변경됩니다.
      </p>
      <FormField
        label="새 이메일"
        type="email"
        autoComplete="email"
        isRequired
        value={email}
        error={emailError ?? undefined}
        onChange={(event) => setEmail(event.target.value)}
        containerClassName="flex-1"
      />
      {hasPassword ? (
        <FormField
          label="현재 비밀번호"
          type="password"
          autoComplete="current-password"
          isRequired
          value={password}
          error={passwordError ?? undefined}
          onChange={(event) => setPassword(event.target.value)}
          containerClassName="flex-1"
        />
      ) : null}
      <Button
        type="submit"
        size="lg"
        isLoading={isPending}
        disabled={isUnchanged}
        className="h-10 shrink-0 px-8"
      >
        확인 메일 보내기
      </Button>
      {formError ? (
        <p role="alert" className="text-sm text-destructive sm:basis-full">
          {formError}
        </p>
      ) : null}
      {sentTo ? (
        <p role="status" className="text-sm text-muted-foreground sm:basis-full">
          {sentTo} 로 확인 메일을 보냈습니다. 메일의 링크를 열면 변경이 완료됩니다.
        </p>
      ) : null}
    </form>
  );
};
